import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  IconButton,
  Stack,
  Chip,
  Alert
} from '@mui/material';
import { Add, Edit, Delete, Publish, Unpublished, Upload, Star } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/api';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt?: string;
  content: string;
  coverImageUrl?: string;
  tags?: string[];
  published: boolean;
  featured?: boolean;
  publishedAt?: string;
  createdAt: string;
}

const API_BASE = API_BASE_URL;

const AdminBlog: React.FC = () => {
  const { token } = useAuth();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    coverImageUrl: '',
    tagsCsv: '',
    published: false,
    featured: false
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [message, setMessage] = useState<string>('');

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/blog`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setPosts(data);
        setMessage('');
      } else {
        if (res.status === 401) {
          setMessage('Authentication failed. Please log in again.');
        } else {
          setMessage(`Failed to load posts: ${res.statusText}`);
        }
      }
    } catch (err: any) {
      console.error('Error loading blog posts:', err);
      setMessage(`Error: ${err.message || 'Failed to load posts'}`);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = () => {
    setEditing(null);
    setForm({ title: '', slug: '', excerpt: '', content: '', coverImageUrl: '', tagsCsv: '', published: false, featured: false });
    setUploadFile(null);
    setOpen(true);
  };

  const handleEdit = (post: BlogPost) => {
    setEditing(post);
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt || '',
      content: post.content,
      coverImageUrl: post.coverImageUrl || '',
      tagsCsv: Array.isArray(post.tags) ? post.tags.join(', ') : '',
      published: !!post.published,
      featured: !!post.featured
    });
    setUploadFile(null);
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this post?')) return;
    await fetch(`${API_BASE}/admin/blog/${id}`, { method: 'DELETE', headers: authHeaders });
    await load();
  };

  const handleTogglePublish = async (post: BlogPost) => {
    await fetch(`${API_BASE}/admin/blog/${post.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ ...post, published: !post.published })
    });
    await load();
  };

  const uploadCoverIfNeeded = async (): Promise<string> => {
    if (!uploadFile) return form.coverImageUrl;
    const fd = new FormData();
    fd.append('image', uploadFile);
    const res = await fetch(`${API_BASE}/api/upload`, { method: 'POST', body: fd });
    if (res.ok) {
      const { imageUrl } = await res.json();
      return imageUrl;
    }
    return form.coverImageUrl;
  };

  const handleSave = async () => {
    const cover = await uploadCoverIfNeeded();
    const payload = {
      title: form.title,
      slug: form.slug,
      excerpt: form.excerpt || null,
      content: form.content,
      coverImageUrl: cover || null,
      tags: form.tagsCsv
        ? form.tagsCsv.split(',').map((s) => s.trim()).filter(Boolean)
        : null,
      published: form.published,
      featured: form.featured
    };

    const url = editing ? `${API_BASE}/admin/blog/${editing.id}` : `${API_BASE}/admin/blog`;
    const method = editing ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      setMessage('Saved');
      setOpen(false);
      await load();
    }
  };

  return (
    <Container sx={{ py: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" gutterBottom>Admin - Blog</Typography>
        {message && <Alert sx={{ mb: 2 }}>{message}</Alert>}
        <Button startIcon={<Add />} variant="contained" onClick={handleAdd}>New Post</Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Featured</TableCell>
                <TableCell>Published</TableCell>
                <TableCell>Published At</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.title}</TableCell>
                  <TableCell>{p.slug}</TableCell>
                  <TableCell>{p.featured ? <Star color="warning" /> : '-'}</TableCell>
                  <TableCell>{p.published ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{p.publishedAt ? new Date(p.publishedAt).toLocaleString() : '-'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {(Array.isArray(p.tags) ? p.tags : []).map(t => (
                        <Chip key={t} size="small" label={t} />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => handleEdit(p)}><Edit /></IconButton>
                    <IconButton color="error" onClick={() => handleDelete(p.id)}><Delete /></IconButton>
                    <IconButton onClick={() => handleTogglePublish(p)}>
                      {p.published ? <Unpublished /> : <Publish />}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Post' : 'New Post'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <TextField
            label="Slug"
            fullWidth
            sx={{ mb: 2 }}
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            helperText="Unique URL path, e.g., transfer-tips-2025"
          />
          <TextField
            label="Excerpt"
            fullWidth
            sx={{ mb: 2 }}
            value={form.excerpt}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
          />
          <TextField
            label="Cover Image URL"
            fullWidth
            sx={{ mb: 1 }}
            value={form.coverImageUrl}
            onChange={(e) => setForm({ ...form, coverImageUrl: e.target.value })}
          />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Button component="label" startIcon={<Upload />}>
              Upload Cover
              <input hidden type="file" accept="image/*" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
            </Button>
            {uploadFile && <Typography variant="body2">{uploadFile.name}</Typography>}
          </Stack>
          <TextField
            label="Tags (comma separated)"
            fullWidth
            sx={{ mb: 2 }}
            value={form.tagsCsv}
            onChange={(e) => setForm({ ...form, tagsCsv: e.target.value })}
          />
          <TextField
            label="Content (HTML supported)"
            fullWidth
            multiline
            minRows={8}
            sx={{ mb: 2 }}
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
          />
          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={<Switch checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />}
              label="Published"
            />
            <FormControlLabel
              control={<Switch checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />}
              label="Featured"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
            <strong>Paid Blog Posts:</strong> To create a sponsored blog post (R500/article), mark as "Featured" and ensure it's published. 
            The client should make payment first, then create the blog post here.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.title || !form.slug || !form.content}>Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminBlog;